import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../types';
import { Loader2, TestTube, Smartphone, Mail, CreditCard, CheckCircle, XCircle, Clock, Activity, AlertCircle } from 'lucide-react';

interface ModuleStatus {
    active: boolean;
    last_success: number | null;
    error: string | null;
}

interface SystemStatus {
    sms: ModuleStatus;
    email: ModuleStatus;
}

interface PayMethod {
    value: string;
    name: string;
    brandImageUrl?: string;
    status: string;
}

interface Props {
    currentUser: UserProfile;
}

const formatDate = (ts: number | null) => ts ? new Date(ts).toLocaleString('pl-PL') : 'Brak danych';

const TestingTab: React.FC<Props> = ({ currentUser }) => {
    const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
    const [smsTest, setSmsTest] = useState({ sender: '7055', phone: '', message: 'Test SMS esoteric.pl' });
    const [smsLog, setSmsLog] = useState<any>(null);
    const [emailTest, setEmailTest] = useState({ from: 'Esoteric <kontakt@esoteric.pl>', to: '', subject: 'Test CMS', content: '<b>Wiadomość testowa</b> esoteric.pl' });
    const [emailLog, setEmailLog] = useState<any>(null);
    const [availableBanks, setAvailableBanks] = useState<PayMethod[]>([]);
    const [payuTest, setPayuTest] = useState({ 
        amount: '1.00', 
        description: 'Test Płatności CMS',
        method: 'redirect' as 'redirect' | 'blik' | 'pbl' | 'card',
        blikCode: '',
        bankCode: '',
        cardData: { number: '', expMonth: '', expYear: '', cvv: '' }
    });
    const [payuLog, setPayuLog] = useState<any>(null);
    const [areBanksLoading, setAreBanksLoading] = useState(false);
    const [testingModule, setTestingModule] = useState<'sms' | 'email' | 'payu' | null>(null);

    useEffect(() => {
        fetchStatus(currentUser);
        if (availableBanks.length === 0) fetchPayMethods(currentUser);
    }, [currentUser]);

    const fetchStatus = async (admin: UserProfile) => {
        try {
            const res = await fetch('/api/cms/module_status', { headers: { 'x-admin-phone': admin.phone } });
            if (res.ok) setSystemStatus(await res.json());
        } catch (e) { console.error(e); }
    };

    const fetchPayMethods = async (admin: UserProfile) => {
        setAreBanksLoading(true);
        try {
            const res = await fetch('/api/cms/test_payu', { headers: { 'x-admin-phone': admin.phone } });
            if (res.ok) {
                const data = await res.json();
                if (data.payByLinks) {
                    const banks = data.payByLinks.filter((m: any) => m.value !== 'c' && m.value !== 'blik' && m.status === 'ENABLED');
                    setAvailableBanks(banks);
                }
            }
        } catch (e) { console.error(e); } finally { setAreBanksLoading(false); }
    };
    
    const handleTestSms = async (e: React.FormEvent) => {
        e.preventDefault();
        setTestingModule('sms');
        try {
          const res = await fetch('/api/cms/test_sms', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser?.phone || '' },
              body: JSON.stringify(smsTest)
          });
          const data = await res.json();
          setSmsLog({ status: res.ok ? 'SUCCESS' : 'ERROR', timestamp: new Date().toLocaleString(), ...data });
          if (res.ok) fetchStatus(currentUser);
        } catch (e: any) { setSmsLog({ status: 'ERROR', timestamp: new Date().toLocaleString(), error: e.message }); }
        setTestingModule(null);
    };
  
    const handleTestEmail = async (e: React.FormEvent) => {
        e.preventDefault();
        setTestingModule('email');
        try {
          const res = await fetch('/api/cms/test_email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser?.phone || '' },
              body: JSON.stringify(emailTest)
          });
          const data = await res.json();
          setEmailLog({ status: res.ok ? 'SUCCESS' : 'ERROR', timestamp: new Date().toLocaleString(), ...data });
           if (res.ok) fetchStatus(currentUser);
        } catch (e: any) { setEmailLog({ status: 'ERROR', timestamp: new Date().toLocaleString(), error: e.message }); }
        setTestingModule(null);
    };
  
    const handleTestPayu = async (e: React.FormEvent) => {
        e.preventDefault();
        setTestingModule('payu');
        try {
          const res = await fetch('/api/cms/test_payu', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser?.phone || '' },
              body: JSON.stringify(payuTest)
          });
          const data = await res.json();
          setPayuLog({ status: res.ok ? 'SUCCESS' : 'ERROR', timestamp: new Date().toLocaleString(), ...data });
          if (res.ok && data.redirectUri) window.open(data.redirectUri, '_blank');
        } catch (e: any) { setPayuLog({ status: 'ERROR', timestamp: new Date().toLocaleString(), error: e.message }); }
        setTestingModule(null);
    };

    return (
        <div className="w-full relative animate-fade-in">
            <h2 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
                <TestTube className="w-5 h-5" /> Module Testing
            </h2>
            <div className="space-y-8">
                 <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg">
                    <h3 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4">Module Status</h3>
                    <div className="grid grid-cols-2 gap-4">
                        {systemStatus?.sms && (
                            <div className={`p-4 rounded border flex items-start gap-3 ${systemStatus.sms.active ? 'bg-green-900/20 border-green-800' : 'bg-red-900/20 border-red-800'}`}>
                                {systemStatus.sms.active ? <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-500 mt-0.5" />}
                                <div>
                                    <p className="font-bold text-white flex items-center gap-2">SMS Module {systemStatus.sms.active ? <span className="text-[9px] px-1.5 py-0.5 bg-green-500/30 text-green-200 rounded-full">ACTIVE</span> : <span className="text-[9px] px-1.5 py-0.5 bg-red-500/30 text-red-200 rounded-full">INACTIVE</span>}</p>
                                    <div className="text-xs text-gray-400 mt-2 flex items-center gap-2"><Clock className="w-3 h-3"/> Last success: {formatDate(systemStatus.sms.last_success)}</div>
                                    {systemStatus.sms.error && <div className="text-xs text-red-400 mt-1 flex items-center gap-2"><AlertCircle className="w-3 h-3"/> {systemStatus.sms.error}</div>}
                                </div>
                            </div>
                        )}
                        {systemStatus?.email && (
                             <div className={`p-4 rounded border flex items-start gap-3 ${systemStatus.email.active ? 'bg-green-900/20 border-green-800' : 'bg-red-900/20 border-red-800'}`}>
                                {systemStatus.email.active ? <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-500 mt-0.5" />}
                                <div>
                                    <p className="font-bold text-white flex items-center gap-2">Email Module {systemStatus.email.active ? <span className="text-[9px] px-1.5 py-0.5 bg-green-500/30 text-green-200 rounded-full">ACTIVE</span> : <span className="text-[9px] px-1.5 py-0.5 bg-red-500/30 text-red-200 rounded-full">INACTIVE</span>}</p>
                                    <div className="text-xs text-gray-400 mt-2 flex items-center gap-2"><Clock className="w-3 h-3"/> Last success: {formatDate(systemStatus.email.last_success)}</div>
                                    {systemStatus.email.error && <div className="text-xs text-red-400 mt-1 flex items-center gap-2"><AlertCircle className="w-3 h-3"/> {systemStatus.email.error}</div>}
                                </div>
                            </div>
                        )}
                    </div>
                 </section>

                 <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg">
                    <h3 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4 flex items-center gap-2"><Smartphone className="w-4 h-4"/> Test SMS (smsapi.pl)</h3>
                    <form onSubmit={handleTestSms} className="grid grid-cols-12 gap-4">
                        <input type="tel" placeholder="Phone Number" value={smsTest.phone} onChange={e => setSmsTest({...smsTest, phone: e.target.value})} className="col-span-10 bg-mystic-inset border border-gray-700 p-2 rounded text-white" />
                        <button type="submit" className="col-span-2 bg-mystic-btn hover:bg-mystic-accent hover:text-black text-white p-2 rounded flex items-center justify-center disabled:opacity-50" disabled={testingModule === 'sms'}>
                            {testingModule === 'sms' ? <Loader2 className="animate-spin w-4 h-4"/> : 'Send'}
                        </button>
                    </form>
                    {smsLog && <pre className="mt-4 bg-black p-4 rounded text-xs overflow-x-auto border border-gray-800"><code>{JSON.stringify(smsLog, null, 2)}</code></pre>}
                 </section>

                 <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg">
                    <h3 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4 flex items-center gap-2"><Smartphone className="w-4 h-4"/> Test SMSYPL</h3>
                    <form
                        onSubmit={async (e) => {
                            e.preventDefault();
                            setTestingModule('sms');
                            try {
                                const res = await fetch('/api/cms/test_smsy', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser?.phone || '' },
                                    body: JSON.stringify(smsTest)
                                });
                                const data = await res.json();
                                setSmsLog({ status: res.ok ? 'SUCCESS' : 'ERROR', timestamp: new Date().toLocaleString(), ...data });
                                if (res.ok) fetchStatus(currentUser);
                            } catch (e: any) {
                                setSmsLog({ status: 'ERROR', timestamp: new Date().toLocaleString(), error: e.message });
                            }
                            setTestingModule(null);
                        }}
                        className="space-y-3"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-[11px] text-gray-500 font-bold mb-1">Sender (shortcode)</label>
                                <select
                                    value={smsTest.sender}
                                    onChange={e => setSmsTest({ ...smsTest, sender: e.target.value })}
                                    className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white text-sm"
                                >
                                    <option value="7055">7055</option>
                                </select>
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-[11px] text-gray-500 font-bold mb-1">Numer odbiorcy</label>
                                <input
                                    type="tel"
                                    placeholder="np. 600600600"
                                    value={smsTest.phone}
                                    onChange={e => setSmsTest({ ...smsTest, phone: e.target.value })}
                                    className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white text-sm"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[11px] text-gray-500 font-bold mb-1">Treść wiadomości</label>
                            <textarea
                                rows={3}
                                value={smsTest.message}
                                onChange={e => setSmsTest({ ...smsTest, message: e.target.value })}
                                className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white text-sm resize-y"
                            />
                        </div>
                        <button type="submit" className="w-full bg-mystic-btn hover:bg-mystic-accent hover:text-black text-white p-2 rounded flex items-center justify-center disabled:opacity-50" disabled={testingModule === 'sms'}>
                            {testingModule === 'sms' ? <Loader2 className="animate-spin w-4 h-4"/> : 'Wyślij SMS testowy (SMSYPL)'}
                        </button>
                    </form>
                    {smsLog && <pre className="mt-4 bg-black p-4 rounded text-xs overflow-x-auto border border-gray-800"><code>{JSON.stringify(smsLog, null, 2)}</code></pre>}
                 </section>

                 <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg">
                    <h3 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4 flex items-center gap-2"><Mail className="w-4 h-4"/> Test Email</h3>
                    <form onSubmit={handleTestEmail} className="grid grid-cols-12 gap-4">
                        <input type="email" placeholder="Recipient Email" value={emailTest.to} onChange={e => setEmailTest({...emailTest, to: e.target.value})} className="col-span-10 bg-mystic-inset border border-gray-700 p-2 rounded text-white" />
                        <button type="submit" className="col-span-2 bg-mystic-btn hover:bg-mystic-accent hover:text-black text-white p-2 rounded flex items-center justify-center disabled:opacity-50" disabled={testingModule === 'email'}>
                             {testingModule === 'email' ? <Loader2 className="animate-spin w-4 h-4"/> : 'Send'}
                        </button>
                    </form>
                    {emailLog && <pre className="mt-4 bg-black p-4 rounded text-xs overflow-x-auto border border-gray-800"><code>{JSON.stringify(emailLog, null, 2)}</code></pre>}
                 </section>

                 <section className="bg-mystic-box border border-gray-800 p-6 rounded-lg">
                    <h3 className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-4 flex items-center gap-2"><CreditCard className="w-4 h-4"/> Test PayU</h3>
                    <form onSubmit={handleTestPayu} className="space-y-4">
                        <div>
                            <label className="text-xs text-gray-400">Amount (PLN)</label>
                            <input type="number" step="0.01" value={payuTest.amount} onChange={e => setPayuTest({...payuTest, amount: e.target.value})} className="w-full bg-mystic-inset border border-gray-700 p-2 rounded text-white mt-1"/>
                        </div>
                        <button type="submit" className="w-full bg-mystic-btn hover:bg-mystic-accent hover:text-black text-white p-2 rounded flex items-center justify-center disabled:opacity-50" disabled={testingModule === 'payu'}>
                             {testingModule === 'payu' ? <Loader2 className="animate-spin w-4 h-4"/> : 'Create Test Payment'}
                        </button>
                    </form>
                    {payuLog && <pre className="mt-4 bg-black p-4 rounded text-xs overflow-x-auto border border-gray-800"><code>{JSON.stringify(payuLog, null, 2)}</code></pre>}
                 </section>

            </div>
        </div>
    );
};

export default TestingTab;
